import { parseTimeString } from "@/utils/datetime";

export interface ValidationRule {
  validate: (value: any, formValues?: Record<string, any>) => boolean | string;
  message: string;
}

export interface ValidationConfig {
  required?: boolean;
  min?: number;
  max?: number;
  pattern?: RegExp;
  message?: string;
  customRules?: ValidationRule[];
}

export const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const commonRules = {
  required: (message: string): ValidationRule => ({
    validate: (value: any) => value !== undefined && value !== "",
    message
  }),

  email: (message = "Invalid email address"): ValidationRule => ({
    validate: (value: string) => !value || emailRegex.test(value),
    message
  }),
  multiEmail: (message = "Invalid email address"): ValidationRule => ({
    validate: (value: string[]) => {
      if (!Array.isArray(value)) return true;
      for (let i = 0; i < value.length; i++) {
        const email = value[i];
        if (email && email.trim() !== "") {
          if (!emailRegex.test(email)) {
            return `Invalid email at position ${i + 1}`;
          }
        }
      }
      return true;
    },
    message
  }),

  number: (config?: {
    min?: number;
    max?: number;
    message?: string;
  }): ValidationRule => ({
    validate: (value: string) => {
      if (!value) return true;
      const num = Number(value);
      if (isNaN(num)) return false;
      if (config?.min !== undefined && num < config.min) return false;
      if (config?.max !== undefined && num > config.max) return false;
      return true;
    },
    message: config?.message || "Invalid number"
  }),

  minLength: (min: number, message?: string): ValidationRule => ({
    validate: (value: string) => !value || value.length >= min,
    message: message || `Must be at least ${min} characters`
  }),

  maxLength: (max: number, message?: string): ValidationRule => ({
    validate: (value: string) => !value || value.length <= max,
    message: message || `Must be at most ${max} characters`
  }),

  pattern: (regex: RegExp, message: string): ValidationRule => ({
    validate: (value: string) => !value || regex.test(value),
    message
  }),

  url: (message = "Invalid URL"): ValidationRule => ({
    validate: (value: string) => {
      if (!value) return true;

      // Accept bare domains (e.g. "example.com") by inferring the protocol for
      // parsing only — the input value is not mutated.
      const hasProtocol = /^https?:\/\//i.test(value);
      const candidate = hasProtocol ? value : `https://${value}`;

      try {
        const parsedUrl = new URL(candidate);
        const hasValidProtocol =
          parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
        // Ensure hostname is valid: no whitespace, at least one character, and contains a dot for domain
        const hostname = parsedUrl.hostname;
        const hasValidHostname =
          hostname.length > 0 &&
          !/\s/.test(hostname) &&
          /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/.test(
            hostname
          );
        // Bare input must be domain-shaped (contain a dot) so we don't accept
        // arbitrary single-token strings like "not-a-url". When the protocol is
        // explicit we still allow dotless hosts (e.g. https://localhost).
        const isDomainShaped = hasProtocol || hostname.includes(".");
        return hasValidProtocol && hasValidHostname && isDomainShaped;
      } catch {
        return false;
      }
    },
    message
  }),

  timeRange: (message: string): ValidationRule => ({
    validate: (endTime: string, formValues?: Record<string, any>) => {
      if (!formValues) return true;
      const startTime = formValues.startTime;
      if (!endTime || !startTime) return true;

      const parsedStart = parseTimeString(startTime);
      const parsedEnd = parseTimeString(endTime);

      if (!parsedStart || !parsedEnd) return true;

      const startMinutes = parsedStart.hour * 60 + parsedStart.minute;
      const endMinutes = parsedEnd.hour * 60 + parsedEnd.minute;

      return endMinutes > startMinutes;
    },
    message
  })
};

export const validateField = (
  value: any,
  config: ValidationConfig,
  formValues?: Record<string, any>
): string | null => {
  const rules: ValidationRule[] = [];

  if (config.required) {
    rules.push(commonRules.required(`Field is required`));
  }

  if (config.min !== undefined || config.max !== undefined) {
    rules.push(
      commonRules.number({
        min: config.min,
        max: config.max,
        message: config.message
      })
    );
  }

  if (config.pattern) {
    rules.push(
      commonRules.pattern(config.pattern, config.message || "Invalid format")
    );
  }

  if (config.customRules) {
    rules.push(...config.customRules);
  }

  for (const rule of rules) {
    const isValid = rule.validate(value, formValues);
    if (isValid !== true) {
      return rule.message;
    }
  }

  return null;
};
